import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Copy, RefreshCw } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { fmt, formatJalaliDate } from '@/lib/format';
import { persianDigits } from '@/features/reports/lib/format';
import { persianDisplayText } from '@/lib/persianUi';
import JalaliDatePicker from '@/components/JalaliDatePicker';
import { PAYMENT_MESSAGES } from '@/features/orders/paymentCopy';

const PAGE_SIZE = 20;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const newIdempotencyKey = () => window.crypto.randomUUID();
const mutationErrorMessage = (apiError, status, fallback) => {
  const detail = typeof apiError?.detail === 'string' ? apiError.detail : '';
  return status === 409 && detail.includes('Idempotency key conflict')
    ? PAYMENT_MESSAGES.idempotencyConflict
    : fallback;
};
const requestMutation = async (request) => {
  try {
    return await request();
  } catch (requestError) {
    console.error('Money operation request failed:', requestError);
    return { data: null, error: requestError, response: null };
  }
};
const PAYMENT_STATUS_LABELS = {
  pending: 'در انتظار',
  verified: 'تأییدشده',
  already_verified: 'تأییدشده',
  failed: 'ناموفق',
  expired: 'منقضی‌شده',
  refunded: 'بازپرداخت‌شده',
};
const PAYMENT_METHOD_LABELS = { debit: 'درگاه بانکی', kalabarg: 'کالابرگ' };
const SUBORDER_STATUS_LABELS = {
  paid: 'در انتظار تحویل',
  payable: 'آمادهٔ تسویه',
  paid_out: 'تسویه‌شده',
  void: 'بدون مبلغ قابل‌پرداخت',
};
const LEDGER_TYPE_LABELS = {
  payable: 'بستانکاری',
  payout: 'پرداخت به فروشنده',
  refund: 'بازپرداخت',
  adjustment: 'اصلاح حساب',
};
const REFUND_REASON_LABELS = {
  return: PAYMENT_MESSAGES.refundReasonReturn,
  rejected_items: PAYMENT_MESSAGES.refundReasonRejected,
  cancelled_after_payment: PAYMENT_MESSAGES.refundReasonCancelled,
  unreversed_payment: PAYMENT_MESSAGES.refundReasonUnreversed,
  cheque_prepayment_cancelled: PAYMENT_MESSAGES.refundReasonCheque,
  seller_direct_refund_recovery: PAYMENT_MESSAGES.refundReasonRecovery,
};
const SHIPPING_PROVIDER_LABELS = {
  sohapeyk: 'سهاپیک',
  flat_rate: 'ارسال استاندارد',
};

function displayDate(value) {
  return value ? formatJalaliDate(value, { includeTime: true }) : '—';
}

function ErrorNotice({ children }) {
  return children ? <div className="admin-table-error" role="alert">{children}</div> : null;
}

function Pager({ page, pages, onChange }) {
  if (pages <= 1) return null;
  return (
    <nav aria-label="صفحه‌بندی" style={{ display: 'flex', justifyContent: 'center', gap: 12, padding: 16 }}>
      <button className="btn btn-outline" disabled={page <= 1} onClick={() => onChange(page - 1)} type="button">قبلی</button>
      <span>{fmt(page)} از {fmt(pages)}</span>
      <button className="btn btn-outline" disabled={page >= pages} onClick={() => onChange(page + 1)} type="button">بعدی</button>
    </nav>
  );
}

function Table({ columns, rows, empty = 'موردی برای نمایش وجود ندارد.' }) {
  return (
    <div className="admin-table-card card">
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead><tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr></thead>
          <tbody>
            {rows.length
              ? rows
              : <tr><td className="admin-table-empty" colSpan={columns.length}>{empty}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function usePagedData(path, query) {
  const [data, setData] = useState({ items: [], pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const queryKey = JSON.stringify(query);
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const params = JSON.parse(queryKey);
    const { data: result, error: apiError } = await apiClient.GET(path, {
      params: { query: { ...params, page: params.page || 1, size: PAGE_SIZE } },
    });
    if (apiError || !result) {
      setError('دریافت اطلاعات انجام نشد. لطفاً دوباره تلاش کنید.');
    } else {
      setData(result);
    }
    setLoading(false);
  }, [path, queryKey]);
  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => { if (active) load(); }, 0);
    return () => { active = false; window.clearTimeout(timer); };
  }, [load]);
  return { data, loading, error, reload: load };
}

export function PaymentsPage() {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({
    status: '', method: '', gateway: '', order_id: '', date_from: '', date_to: '',
  });
  const query = {
    page,
    ...(filter.status && { status: filter.status }),
    ...(filter.method && { method: filter.method }),
    ...(filter.gateway && { gateway: filter.gateway }),
    ...(filter.order_id && { order_id: filter.order_id.trim() }),
    ...(filter.date_from && { date_from: `${filter.date_from}T00:00:00Z` }),
    ...(filter.date_to && { date_to: `${filter.date_to}T23:59:59Z` }),
  };
  const { data, loading, error } = usePagedData('/api/v1/super_admin/payments', query);
  const change = (key) => (event) => {
    setPage(1);
    setFilter((current) => ({ ...current, [key]: event.target.value }));
  };

  return (
    <section>
      <header className="admin-page-header"><div><h1>پرداخت‌ها</h1><p>تراکنش‌های پرداخت سفارش‌ها</p></div></header>
      <div className="card" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: 16, marginBottom: 16 }}>
        <label>وضعیت
          <select value={filter.status} onChange={change('status')}>
            <option value="">همه</option>
            {Object.entries(PAYMENT_STATUS_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <label>روش پرداخت
          <select value={filter.method} onChange={change('method')}>
            <option value="">همه</option>
            {Object.entries(PAYMENT_METHOD_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <label>درگاه
          <select value={filter.gateway} onChange={change('gateway')}>
            <option value="">همه</option><option value="sep">سامان</option><option value="mock">آزمایشی</option>
          </select>
        </label>
        <label>شماره سفارش<input value={filter.order_id} onChange={change('order_id')} /></label>
        <label>از تاریخ
          <JalaliDatePicker
            aria-label="از تاریخ شمسی"
            onChange={(value) => {
              setPage(1);
              setFilter((current) => ({ ...current, date_from: value }));
            }}
            rangeStart={filter.date_from}
            rangeEnd={filter.date_to}
            value={filter.date_from}
          />
        </label>
        <label>تا تاریخ
          <JalaliDatePicker
            aria-label="تا تاریخ شمسی"
            onChange={(value) => {
              setPage(1);
              setFilter((current) => ({ ...current, date_to: value }));
            }}
            rangeStart={filter.date_from}
            rangeEnd={filter.date_to}
            value={filter.date_to}
          />
        </label>
      </div>
      <ErrorNotice>{error}</ErrorNotice>
      {loading ? <p role="status">در حال دریافت پرداخت‌ها...</p> : (
        <Table columns={['تاریخ', 'سفارش', 'موبایل مشتری', 'مبلغ', 'روش', 'وضعیت', 'شناسهٔ درگاه']}
          rows={(data.items ?? []).map((item) => (
            <tr key={item.id}>
              <td>{displayDate(item.created_at)}</td><td>{item.order_id ?? '—'}</td><td>{persianDigits(item.customer_mobile) || '—'}</td>
              <td>{fmt(item.amount)} تومان</td>
              <td>{PAYMENT_METHOD_LABELS[item.method] ?? 'نامشخص'}</td>
              <td>{PAYMENT_STATUS_LABELS[item.status] ?? 'نامشخص'}</td>
              <td>{item.gateway_ref_id ?? item.ref_id ?? '—'}</td>
            </tr>
          ))}
        />
      )}
      <Pager page={page} pages={data.pages ?? 0} onChange={setPage} />
    </section>
  );
}

export function VendorSettlementsPage() {
  const [page, setPage] = useState(1);
  const { data, loading, error } = usePagedData('/api/v1/super_admin/settlements/vendors', { page });
  return (
    <section>
      <header className="admin-page-header"><div><h1>تسویه با فروشندگان</h1><p>ماندهٔ حساب فروشندگان و سفارش‌های آمادهٔ پرداخت</p></div></header>
      <ErrorNotice>{error}</ErrorNotice>
      {loading ? <p role="status">در حال دریافت مانده‌ها...</p> : (
        <Table columns={['فروشگاه', 'شمارهٔ شبا', 'مبلغ قابل‌پرداخت', 'زیرسفارش‌های قابل‌پرداخت', 'در انتظار تحویل', 'وضعیت', '']}
          rows={(data.items ?? []).map((vendor) => (
            <tr key={vendor.vendor_id}>
              <td>{persianDisplayText(vendor.shop_name, 'فروشگاه')}</td><td>{vendor.shaba || '—'}</td>
              <td>{fmt(vendor.payable_total)} تومان</td><td>{fmt(vendor.payable_count)}</td>
              <td>{fmt(vendor.awaiting_count)}</td>
              <td>{vendor.has_debt ? <span className="order-status-badge order-status-badge--danger">بدهکار</span> : '—'}</td>
              <td><Link to={`/super-admin/settlements/vendors/${vendor.vendor_id}`}>جزئیات</Link></td>
            </tr>
          ))}
        />
      )}
      <Pager page={page} pages={data.pages ?? 0} onChange={setPage} />
    </section>
  );
}

export function VendorSettlementDetailPage() {
  const { vendorId } = useParams();
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [bankReference, setBankReference] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [payoutNotice, setPayoutNotice] = useState('');
  const [payoutIdempotencyKey, setPayoutIdempotencyKey] = useState('');
  const [adjustment, setAdjustment] = useState({ amount: '', reason: '' });
  const [adjustmentKey, setAdjustmentKey] = useState(newIdempotencyKey);
  const [adjustmentBusy, setAdjustmentBusy] = useState(false);
  const [adjustmentNotice, setAdjustmentNotice] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const { data, error: apiError } = await apiClient.GET('/api/v1/super_admin/settlements/vendors/{vendor_id}', {
      params: { path: { vendor_id: vendorId }, query: { page, size: PAGE_SIZE } },
    });
    if (apiError || !data) setError('دریافت جزئیات تسویه انجام نشد.');
    else setDetail(data);
    setLoading(false);
  }, [page, vendorId]);
  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => { if (active) load(); }, 0);
    return () => { active = false; window.clearTimeout(timer); };
  }, [load]);

  const submitPayout = async (event) => {
    event.preventDefault();
    if (!bankReference.trim()) { setError('ثبت شمارهٔ پیگیری بانکی الزامی است.'); return; }
    if (!(Number(detail?.balance) > 0)) { setError('موجودی قابل‌پرداخت مثبت نیست.'); return; }
    if (!window.confirm(`${PAYMENT_MESSAGES.confirmPayoutAmount} ${fmt(balance)} تومان`)) return;
    setSubmitting(true);
    setError('');
    setPayoutNotice('');
    const { error: apiError, response } = await requestMutation(() => apiClient.POST('/api/v1/super_admin/settlements/vendors/{vendor_id}/payouts', {
      params: { path: { vendor_id: vendorId } },
      body: { expected_amount: detail.balance, bank_reference: bankReference.trim(), note: note.trim() || null },
      headers: { 'Idempotency-Key': payoutIdempotencyKey },
    }));
    if (response?.status === 409) {
      setModalOpen(false);
      await load();
      setError(mutationErrorMessage(apiError, response.status, PAYMENT_MESSAGES.payoutConflict));
    } else if (apiError) {
      setError('ثبت پرداخت انجام نشد. اطلاعات را بررسی و دوباره تلاش کنید.');
    } else {
      setModalOpen(false);
      setBankReference('');
      setNote('');
      setPayoutIdempotencyKey('');
      setPayoutNotice(PAYMENT_MESSAGES.payoutComplete);
      await load();
    }
    setSubmitting(false);
  };
  const submitAdjustment = async (event) => {
    event.preventDefault();
    const amount = Number(adjustment.amount);
    if (!Number.isFinite(amount) || amount === 0 || !adjustment.reason.trim()) {
      setError(PAYMENT_MESSAGES.adjustmentFieldsRequired);
      return;
    }
    if (!window.confirm(`${PAYMENT_MESSAGES.confirmAdjustmentAmount} ${fmt(amount)} تومان`)) return;
    setAdjustmentBusy(true);
    setError('');
    setAdjustmentNotice('');
    const { error: apiError, response } = await requestMutation(() => apiClient.POST(
      '/api/v1/super_admin/settlements/vendors/{vendor_id}/adjustments',
      {
        params: { path: { vendor_id: vendorId } },
        headers: { 'Idempotency-Key': adjustmentKey },
        body: { amount, reason: adjustment.reason.trim() },
      },
    ));
    if (apiError) {
      setError(mutationErrorMessage(apiError, response?.status, PAYMENT_MESSAGES.adjustmentFailed));
    } else {
      setAdjustment({ amount: '', reason: '' });
      setAdjustmentKey(newIdempotencyKey());
      setAdjustmentNotice(PAYMENT_MESSAGES.adjustmentComplete);
      await load();
    }
    setAdjustmentBusy(false);
  };
  const copySheba = async () => {
    try { await navigator.clipboard.writeText(detail?.shaba ?? ''); }
    catch { setError('کپی شمارهٔ شبا انجام نشد.'); }
  };
  const ledger = detail?.ledger ?? { items: [], pages: 0 };
  const balance = Number(detail?.balance ?? 0);

  return (
    <section>
      <Link to="/super-admin/settlements">بازگشت به تسویه‌ها</Link>
      <header className="admin-page-header"><div><h1>{persianDisplayText(detail?.shop_name, 'جزئیات فروشنده')}</h1><p>شمارهٔ شبا: {detail?.shaba || 'ثبت نشده'}</p></div></header>
      <ErrorNotice>{error}</ErrorNotice>
      {payoutNotice && <p role="status">{payoutNotice}</p>}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <strong>ماندهٔ قابل‌پرداخت: {fmt(balance)} تومان</strong>
        <button className="btn btn-primary" disabled={balance <= 0} onClick={() => {
          setPayoutIdempotencyKey(newIdempotencyKey());
          setModalOpen(true);
        }} type="button">{PAYMENT_MESSAGES.payoutSubmit}</button>
        {balance <= 0 && <p>{balance < 0 ? 'به‌دلیل بدهی، پرداخت به فروشنده امکان‌پذیر نیست.' : 'مبلغ قابل‌پرداختی برای این فروشنده وجود ندارد.'}</p>}
      </div>
      <form className="card" onSubmit={submitAdjustment} style={{ display: 'grid', gap: 12, padding: 16, marginBottom: 16 }}>
        <h2>{PAYMENT_MESSAGES.adjustmentTitle}</h2>
        {adjustmentNotice && <p role="status">{adjustmentNotice}</p>}
        <label>{PAYMENT_MESSAGES.adjustmentAmountLabel}<input inputMode="numeric" step="1" type="number" value={adjustment.amount} onChange={(event) => {
          setAdjustment((current) => ({ ...current, amount: event.target.value }));
          setAdjustmentKey(newIdempotencyKey());
        }} /></label>
        <label>{PAYMENT_MESSAGES.adjustmentReasonLabel}<input maxLength={255} value={adjustment.reason} onChange={(event) => {
          setAdjustment((current) => ({ ...current, reason: event.target.value }));
          setAdjustmentKey(newIdempotencyKey());
        }} /></label>
        <button className="btn btn-primary" disabled={adjustmentBusy} type="submit">
          {adjustmentBusy ? PAYMENT_MESSAGES.mutationPending : PAYMENT_MESSAGES.adjustmentSubmit}
        </button>
      </form>
      {loading ? <p role="status">در حال دریافت گردش حساب...</p> : (
        <>
          <h2>گردش حساب</h2>
          <Table columns={['تاریخ', 'شرح', 'نوع', 'مبلغ', 'شماره سفارش']}
            rows={(ledger.items ?? []).map((entry) => (
              <tr key={entry.id}><td>{displayDate(entry.created_at)}</td><td>{persianDisplayText(entry.description, 'شرح گردش حساب')}</td>
                <td>{LEDGER_TYPE_LABELS[entry.transaction_type] ?? 'تغییر مانده'}</td>
                <td>{fmt(entry.amount)} تومان</td><td>{entry.order_id ?? '—'}</td></tr>
            ))}
          />
          <Pager page={page} pages={ledger.pages ?? 0} onChange={setPage} />
          <h2>زیرسفارش‌ها</h2>
          {Object.entries(detail?.suborders_by_status ?? {}).map(([status, count]) => (
            <section key={status}>
              <h3>{SUBORDER_STATUS_LABELS[status] ?? 'وضعیت نامشخص'} ({fmt(count)})</h3>
              <Table columns={['شماره سفارش', 'مبلغ خالص', 'مبلغ برگشتی', 'تاریخ تحویل', 'تاریخ تسویه']}
                rows={(detail?.suborders ?? []).filter((suborder) => suborder.status === status).map((suborder) => (
                  <tr key={suborder.id}><td>{suborder.order_id}</td><td>{fmt(suborder.net_amount)} تومان</td>
                    <td>{fmt(suborder.returned_amount)} تومان</td><td>{displayDate(suborder.delivered_at)}</td>
                    <td>{displayDate(suborder.paid_out_at)}</td></tr>
                ))}
              />
            </section>
          ))}
        </>
      )}
      {modalOpen && (
        <div className="admin-modal-backdrop" onClick={() => setModalOpen(false)}>
          <section aria-modal="true" className="admin-modal" onClick={(event) => event.stopPropagation()} role="dialog">
            <div className="admin-modal-header"><h2 className="admin-modal-title">ثبت پرداخت به فروشنده</h2></div>
            <form className="admin-modal-body" onSubmit={submitPayout}>
              <p>فروشگاه: {persianDisplayText(detail?.shop_name, 'فروشنده')}</p>
              <p>شمارهٔ شبا: <bdi>{detail?.shaba || '—'}</bdi>
                <button aria-label="کپی شماره شبا" onClick={copySheba} type="button"><Copy size={16} /></button>
              </p>
              <label>مبلغ پرداخت (تومان)<input readOnly value={fmt(balance)} /></label>
              <label>{PAYMENT_MESSAGES.payoutBankReferenceLabel}<input maxLength={128} required value={bankReference} onChange={(event) => {
                setBankReference(event.target.value);
                setPayoutIdempotencyKey(newIdempotencyKey());
              }} /></label>
              <label>{PAYMENT_MESSAGES.payoutNoteLabel}<input value={note} onChange={(event) => {
                setNote(event.target.value);
                setPayoutIdempotencyKey(newIdempotencyKey());
              }} /></label>
              <div className="admin-modal-actions">
                <button className="btn btn-outline" onClick={() => setModalOpen(false)} type="button">انصراف</button>
                <button className="btn btn-primary" disabled={submitting || balance <= 0} type="submit">{submitting ? 'در حال ثبت...' : 'ثبت پرداخت'}</button>
              </div>
            </form>
          </section>
        </div>
      )}
    </section>
  );
}

export function PayoutHistoryPage() {
  const [page, setPage] = useState(1);
  const [vendorId, setVendorId] = useState('');
  const query = { page, ...(vendorId.trim() && { vendor_id: vendorId.trim() }) };
  const { data, loading, error } = usePagedData('/api/v1/super_admin/settlements/payouts', query);
  return (
    <section>
      <header className="admin-page-header"><div><h1>تاریخچه پرداخت‌های انجام‌شده</h1><p>سوابق پرداخت دستی به فروشندگان</p></div></header>
      <label>شناسهٔ فروشنده<input value={vendorId} onChange={(event) => { setVendorId(event.target.value); setPage(1); }} /></label>
      <ErrorNotice>{error}</ErrorNotice>
      {loading ? <p role="status">در حال دریافت سوابق...</p> : (
        <Table columns={['تاریخ پرداخت', 'فروشگاه', 'مبلغ', 'شمارهٔ پیگیری بانکی', 'شمارهٔ شبا', 'یادداشت']}
          rows={(data.items ?? []).map((payout) => (
            <tr key={payout.id}><td>{displayDate(payout.paid_at)}</td><td>{persianDisplayText(payout.vendor_name_snapshot, 'فروشگاه')}</td>
              <td>{fmt(payout.amount)} تومان</td><td>{payout.bank_reference}</td><td>{payout.shaba_snapshot}</td><td>{payout.note || '—'}</td></tr>
          ))}
        />
      )}
      <Pager page={page} pages={data.pages ?? 0} onChange={setPage} />
    </section>
  );
}

export function RefundsPage() {
  const [due, setDue] = useState([]);
  const [data, setData] = useState({ items: [], pages: 0 });
  const [page, setPage] = useState(1);
  const [form, setForm] = useState({ order_id: '', vendor_order_id: '', reason: 'rejected_items', amount: '', bank_reference: '', note: '' });
  const [refundIdempotencyKey, setRefundIdempotencyKey] = useState(newIdempotencyKey);
  const [recoveryForm, setRecoveryForm] = useState({
    order_id: '', vendor_order_id: '', amount: '', bank_reference: '', note: '',
  });
  const [recoveryIdempotencyKey, setRecoveryIdempotencyKey] = useState(newIdempotencyKey);
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    const [dueResult, historyResult] = await Promise.all([
      apiClient.GET('/api/v1/super_admin/settlements/refunds-due'),
      apiClient.GET('/api/v1/super_admin/settlements/refunds', { params: { query: { page, size: PAGE_SIZE } } }),
    ]);
    if (dueResult.error || historyResult.error || !historyResult.data) setError('دریافت اطلاعات بازگشت وجه انجام نشد.');
    else { setDue(dueResult.data ?? []); setData(historyResult.data); setError(''); }
    setLoading(false);
  }, [page]);
  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => { if (active) load(); }, 0);
    return () => { active = false; window.clearTimeout(timer); };
  }, [load]);
  const setField = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
    setRefundIdempotencyKey(newIdempotencyKey());
  };
  const setRecoveryField = (key) => (event) => {
    setRecoveryForm((current) => ({ ...current, [key]: event.target.value }));
    setRecoveryIdempotencyKey(newIdempotencyKey());
  };
  const submit = async (event) => {
    event.preventDefault();
    if (!UUID_PATTERN.test(form.order_id.trim())) {
      setError('شناسهٔ سفارش معتبر نیست.');
      return;
    }
    if (form.vendor_order_id.trim() && !UUID_PATTERN.test(form.vendor_order_id.trim())) {
      setError('شناسهٔ زیرسفارش معتبر نیست.');
      return;
    }
    if (!Number.isFinite(Number(form.amount)) || Number(form.amount) <= 0 || !form.bank_reference.trim()) {
      setError('شماره سفارش، مبلغ مثبت و شمارهٔ پیگیری بانکی را کامل کنید.');
      return;
    }
    if (form.reason === 'return' && !form.vendor_order_id.trim()) {
      setError('برای برگشت کالا، شناسهٔ زیرسفارش الزامی است.');
      return;
    }
    if (!window.confirm(`${PAYMENT_MESSAGES.confirmRefundAmount} ${fmt(form.amount)} تومان`)) return;
    setSubmitting(true); setError(''); setNotice('');
    const { error: apiError, response } = await requestMutation(() => apiClient.POST('/api/v1/super_admin/settlements/refunds', {
      headers: { 'Idempotency-Key': refundIdempotencyKey },
      body: {
        order_id: form.order_id.trim(),
        ...(form.vendor_order_id.trim() && { vendor_order_id: form.vendor_order_id.trim() }),
        reason: form.reason,
        amount: Number(form.amount),
        bank_reference: form.bank_reference.trim(),
        note: form.note.trim() || null,
      },
    }));
    if (apiError) {
      setError(mutationErrorMessage(apiError, response?.status, PAYMENT_MESSAGES.refundFailed));
    } else {
      setNotice(PAYMENT_MESSAGES.refundComplete);
      setForm({ order_id: '', vendor_order_id: '', reason: 'rejected_items', amount: '', bank_reference: '', note: '' });
      setRefundIdempotencyKey(newIdempotencyKey());
      await load();
    }
    setSubmitting(false);
  };
  const submitRecovery = async (event) => {
    event.preventDefault();
    if (
      !UUID_PATTERN.test(recoveryForm.order_id.trim())
      || !UUID_PATTERN.test(recoveryForm.vendor_order_id.trim())
      || !Number.isFinite(Number(recoveryForm.amount))
      || Number(recoveryForm.amount) <= 0
    ) {
      setError(PAYMENT_MESSAGES.recoveryFieldsRequired);
      return;
    }
    if (!window.confirm(`${PAYMENT_MESSAGES.confirmRecoveryAmount} ${fmt(recoveryForm.amount)} تومان`)) return;
    setRecoverySubmitting(true);
    setError('');
    setNotice('');
    const { error: apiError, response } = await requestMutation(() => apiClient.POST(
      '/api/v1/super_admin/settlements/seller-direct-refund-recoveries',
      {
        headers: { 'Idempotency-Key': recoveryIdempotencyKey },
        body: {
          order_id: recoveryForm.order_id.trim(),
          vendor_order_id: recoveryForm.vendor_order_id.trim(),
          amount: Number(recoveryForm.amount),
          bank_reference: recoveryForm.bank_reference.trim() || null,
          note: recoveryForm.note.trim() || null,
        },
      },
    ));
    if (apiError) {
      setError(mutationErrorMessage(apiError, response?.status, PAYMENT_MESSAGES.recoveryFailed));
    } else {
      setNotice(PAYMENT_MESSAGES.recoveryComplete);
      setRecoveryForm({ order_id: '', vendor_order_id: '', amount: '', bank_reference: '', note: '' });
      setRecoveryIdempotencyKey(newIdempotencyKey());
      await load();
    }
    setRecoverySubmitting(false);
  };

  return (
    <section>
      <header className="admin-page-header"><div><h1>{PAYMENT_MESSAGES.refundTitle}</h1><p>{PAYMENT_MESSAGES.refundIntro}</p></div></header>
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        {PAYMENT_MESSAGES.refundManualNote}
      </div>
      <h2>{PAYMENT_MESSAGES.pendingRefundsTitle}</h2>
      <ErrorNotice>{error}</ErrorNotice>
      {loading ? <p role="status">{PAYMENT_MESSAGES.refundRequestsLoading}</p> : (
        <Table columns={[PAYMENT_MESSAGES.orderNumberColumn, PAYMENT_MESSAGES.customerMobileColumn, PAYMENT_MESSAGES.refundReasonColumn, PAYMENT_MESSAGES.liabilityTotalColumn, PAYMENT_MESSAGES.refundedAmountColumn, PAYMENT_MESSAGES.remainingRefundAmountColumn, PAYMENT_MESSAGES.orderDateColumn]}
          rows={due.map((item) => (
            <tr key={item.order_id}><td>{item.order_id}</td><td>{persianDigits(item.customer_mobile) || '—'}</td>
              <td>{REFUND_REASON_LABELS[item.reason] ?? item.reason}</td><td>{fmt(item.liability_total)} تومان</td>
              <td>{fmt(item.refunded_total)} تومان</td>
              <td><button className="btn btn-outline" onClick={() => {
                setForm((current) => ({ ...current, order_id: item.order_id, amount: String(item.amount_due), reason: item.reason }));
                setRefundIdempotencyKey(newIdempotencyKey());
              }} type="button">{fmt(item.amount_due)} تومان · {PAYMENT_MESSAGES.selectRefundAmount}</button></td>
              <td>{displayDate(item.order_created_at)}</td></tr>
          ))}
        />
      )}
      <h2>{PAYMENT_MESSAGES.refundRegistrationTitle}</h2>
      {notice && <div className="admin-modal-banner success" role="status">{notice}</div>}
      <form className="card" onSubmit={submit} style={{ display: 'grid', gap: 12, padding: 16 }}>
        <label>{PAYMENT_MESSAGES.refundOrderNumberLabel}<input required value={form.order_id} onChange={setField('order_id')} /></label>
        <label>{PAYMENT_MESSAGES.refundVendorOrderLabel}<input value={form.vendor_order_id} onChange={setField('vendor_order_id')} /></label>
        <label>{PAYMENT_MESSAGES.refundReasonLabel}
          <select value={form.reason} onChange={setField('reason')}>
            {Object.entries(REFUND_REASON_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>{PAYMENT_MESSAGES.refundAmountLabel}<input required min="1" step="1" type="number" value={form.amount} onChange={setField('amount')} /></label>
        <label>{PAYMENT_MESSAGES.refundBankReferenceLabel}<input required maxLength={128} value={form.bank_reference} onChange={setField('bank_reference')} /></label>
        <label>{PAYMENT_MESSAGES.refundNoteLabel}<textarea value={form.note} onChange={setField('note')} /></label>
        <button className="btn btn-primary" disabled={submitting} type="submit">{submitting ? PAYMENT_MESSAGES.mutationPending : PAYMENT_MESSAGES.refundRegistrationTitle}</button>
      </form>
      <h2>{PAYMENT_MESSAGES.submittedRefundsTitle}</h2>
      {loading ? <p role="status">{PAYMENT_MESSAGES.refundHistoryLoading}</p> : (
        <Table columns={[PAYMENT_MESSAGES.auditDateColumn, PAYMENT_MESSAGES.orderNumberColumn, PAYMENT_MESSAGES.refundReasonColumn, PAYMENT_MESSAGES.refundAmountLabel, PAYMENT_MESSAGES.refundBankReferenceLabel, PAYMENT_MESSAGES.refundNoteLabel]}
          rows={(data.items ?? []).map((refund) => (
            <tr key={refund.id}><td>{displayDate(refund.refunded_at)}</td><td>{refund.order_id}</td>
              <td>{REFUND_REASON_LABELS[refund.reason] ?? PAYMENT_MESSAGES.unknownRefundReason}</td><td>{fmt(refund.amount)} تومان</td>
              <td>{refund.bank_reference || '—'}</td><td>{refund.note || '—'}</td></tr>
          ))}
        />
      )}
      <Pager page={page} pages={data.pages ?? 0} onChange={setPage} />
      <h2>{PAYMENT_MESSAGES.recoveryTitle}</h2>
      <form className="card" onSubmit={submitRecovery} style={{ display: 'grid', gap: 12, padding: 16 }}>
        <label>{PAYMENT_MESSAGES.orderIdLabel}<input required value={recoveryForm.order_id} onChange={setRecoveryField('order_id')} /></label>
        <label>{PAYMENT_MESSAGES.vendorOrderIdLabel}<input required value={recoveryForm.vendor_order_id} onChange={setRecoveryField('vendor_order_id')} /></label>
        <label>{PAYMENT_MESSAGES.recoveryAmountLabel}<input required min="1" step="1" type="number" value={recoveryForm.amount} onChange={setRecoveryField('amount')} /></label>
        <label>{PAYMENT_MESSAGES.bankReferenceLabel}<input maxLength={128} value={recoveryForm.bank_reference} onChange={setRecoveryField('bank_reference')} /></label>
        <label>{PAYMENT_MESSAGES.noteLabel}<textarea value={recoveryForm.note} onChange={setRecoveryField('note')} /></label>
        <button className="btn btn-primary" disabled={recoverySubmitting} type="submit">
          {recoverySubmitting ? PAYMENT_MESSAGES.mutationPending : PAYMENT_MESSAGES.recoverySubmit}
        </button>
      </form>
    </section>
  );
}

export function ShippingPayablesPage() {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState([]);
  const [reference, setReference] = useState('');
  const [shippingIdempotencyKey, setShippingIdempotencyKey] = useState(newIdempotencyKey);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: apiError } = await apiClient.GET('/api/v1/super_admin/settlements/shipping-payables');
    if (apiError || !data) setError('دریافت هزینه‌های ارسال انجام نشد.');
    else { setGroups(data); setSelected([]); setError(''); }
    setLoading(false);
  }, []);
  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => { if (active) load(); }, 0);
    return () => { active = false; window.clearTimeout(timer); };
  }, [load]);
  const changeSelection = (shipmentId) => {
    setSelected((current) => (
      current.includes(shipmentId) ? current.filter((id) => id !== shipmentId) : [...current, shipmentId]
    ));
    setShippingIdempotencyKey(newIdempotencyKey());
  };
  const submit = async () => {
    if (!selected.length || !reference.trim()) {
      setError('حداقل یک مرسوله و شمارهٔ پیگیری پرداخت را وارد کنید.');
      return;
    }
    const selectedAmount = groups.flatMap((group) => group.shipments ?? [])
      .filter((shipment) => selected.includes(shipment.id))
      .reduce((total, shipment) => total + Number(shipment.amount), 0);
    if (!window.confirm(`${PAYMENT_MESSAGES.confirmShippingAmount} ${fmt(selectedAmount)} تومان`)) return;
    setSubmitting(true); setError('');
    const { error: apiError, response } = await requestMutation(() => apiClient.POST('/api/v1/super_admin/settlements/shipping-payables/mark-paid', {
      headers: { 'Idempotency-Key': shippingIdempotencyKey },
      body: { shipment_ids: selected, payment_reference: reference.trim() },
    }));
    if (apiError) setError(mutationErrorMessage(apiError, response?.status, PAYMENT_MESSAGES.shippingPaymentFailed));
    else { setReference(''); setShippingIdempotencyKey(newIdempotencyKey()); await load(); }
    setSubmitting(false);
  };
  return (
    <section>
      <header className="admin-page-header"><div><h1>هزینه ارسال</h1><p>مرسوله‌های پرداخت‌نشده به تفکیک شرکت ارسال</p></div>
        <button className="btn btn-outline" onClick={load} type="button"><RefreshCw size={16} /> تازه‌سازی</button>
      </header>
      <ErrorNotice>{error}</ErrorNotice>
      {loading ? <p role="status">در حال دریافت مرسوله‌ها...</p> : groups.map((group) => (
        <section className="card" key={group.provider_code} style={{ padding: 16, marginBottom: 16 }}>
          <h2>{SHIPPING_PROVIDER_LABELS[group.provider_code] ?? 'شرکت ارسال'}</h2>
          <p>{fmt(group.shipment_count)} مرسوله · مجموع {fmt(group.total_amount)} تومان</p>
          {(group.shipments ?? []).map((shipment) => (
            <label key={shipment.id} style={{ display: 'flex', gap: 8, padding: 8 }}>
              <input checked={selected.includes(shipment.id)} onChange={() => changeSelection(shipment.id)} type="checkbox" />
              <span>سفارش {shipment.order_id} · {fmt(shipment.amount)} تومان</span>
            </label>
          ))}
        </section>
      ))}
      <div className="card" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: 16 }}>
        <label>شمارهٔ پیگیری پرداخت<input maxLength={128} value={reference} onChange={(event) => {
          setReference(event.target.value);
          setShippingIdempotencyKey(newIdempotencyKey());
        }} /></label>
        <button className="btn btn-primary" disabled={submitting || selected.length === 0} onClick={submit} type="button">
          {submitting ? 'در حال ثبت...' : 'ثبت پرداخت'}
        </button>
      </div>
    </section>
  );
}

export function SellerGatewayAdminPage() {
  const [vendorId, setVendorId] = useState('');
  const [gatewayState, setGatewayState] = useState(null);
  const [negativeBalanceLimit, setNegativeBalanceLimit] = useState(null);
  const [terminalId, setTerminalId] = useState('');
  const [credentialRef, setCredentialRef] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [configIdempotencyKey, setConfigIdempotencyKey] = useState(newIdempotencyKey);
  const [credentialSetKey, setCredentialSetKey] = useState(newIdempotencyKey);
  const [credentialRotateKey, setCredentialRotateKey] = useState(newIdempotencyKey);

  useEffect(() => {
    let active = true;
    apiClient.GET('/api/v1/super_admin/settlements/negative-balance-limit')
      .then(({ data, error: apiError }) => {
        if (!active) return;
        if (apiError || !data) setError(PAYMENT_MESSAGES.gatewayConfigFailed);
        else setNegativeBalanceLimit(data.negative_balance_limit);
      })
      .catch(() => {
        if (active) setError(PAYMENT_MESSAGES.gatewayConfigFailed);
      });
    return () => { active = false; };
  }, []);

  const loadGateway = async (event) => {
    event?.preventDefault();
    const id = vendorId.trim();
    if (!UUID_PATTERN.test(id)) {
      setError(PAYMENT_MESSAGES.vendorIdInvalid);
      return;
    }
    setLoading(true);
    setError('');
    setNotice('');
    let result;
    try {
      result = await apiClient.GET(
        '/api/v1/super_admin/vendors/{vendor_id}/seller-gateway',
        { params: { path: { vendor_id: id } } },
      );
    } catch (requestError) {
      console.error('Seller gateway configuration request failed:', requestError);
      setGatewayState(null);
      setError(PAYMENT_MESSAGES.gatewayConfigFailed);
      setLoading(false);
      return;
    }
    const { data, error: apiError } = result;
    if (apiError || !data) {
      setGatewayState(null);
      setError(PAYMENT_MESSAGES.gatewayConfigFailed);
    } else {
      setGatewayState(data);
      setEnabled(data.seller_gateway_enabled);
      setTerminalId('');
      setCredentialRef('');
    }
    setLoading(false);
  };

  const saveConfig = async (event) => {
    event.preventDefault();
    if (!window.confirm(PAYMENT_MESSAGES.confirmGatewayConfig)) return;
    setPending(true);
    setError('');
    setNotice('');
    const { data, error: apiError, response } = await requestMutation(() => apiClient.PATCH(
      '/api/v1/super_admin/vendors/{vendor_id}/seller-gateway',
      {
        params: { path: { vendor_id: vendorId.trim() } },
        headers: { 'Idempotency-Key': configIdempotencyKey },
        body: {
          seller_gateway_enabled: enabled,
          seller_gateway_terminal_id: terminalId.trim() || null,
        },
      },
    ));
    if (apiError || !data) {
      setError(mutationErrorMessage(apiError, response?.status, PAYMENT_MESSAGES.gatewayConfigFailed));
    } else {
      setGatewayState(data);
      setEnabled(data.seller_gateway_enabled);
      setTerminalId('');
      setConfigIdempotencyKey(newIdempotencyKey());
      setNotice(PAYMENT_MESSAGES.gatewayConfigSaved);
    }
    setPending(false);
  };

  const updateCredential = async (action) => {
    if (!credentialRef.trim()) {
      setError(PAYMENT_MESSAGES.credentialRefRequired);
      return;
    }
    setPending(true);
    setError('');
    setNotice('');
    const path = action === 'rotate'
      ? '/api/v1/super_admin/vendors/{vendor_id}/seller-gateway/rotate-credential-reference'
      : '/api/v1/super_admin/vendors/{vendor_id}/seller-gateway/credential-reference';
    const method = action === 'rotate' ? 'POST' : 'PATCH';
    const { data, error: apiError, response } = await requestMutation(() => apiClient[method](path, {
      params: { path: { vendor_id: vendorId.trim() } },
      headers: {
        'Idempotency-Key': action === 'rotate' ? credentialRotateKey : credentialSetKey,
      },
      body: { credential_ref: credentialRef.trim() },
    }));
    if (apiError || !data) {
      setError(mutationErrorMessage(apiError, response?.status, PAYMENT_MESSAGES.gatewayConfigFailed));
    } else {
      setGatewayState(data);
      setCredentialRef('');
      setCredentialSetKey(newIdempotencyKey());
      setCredentialRotateKey(newIdempotencyKey());
      setNotice(PAYMENT_MESSAGES.gatewayCredentialSaved);
    }
    setPending(false);
  };

  return (
    <section>
      <header className="admin-page-header">
        <div><h1>{PAYMENT_MESSAGES.gatewayConfigTitle}</h1></div>
      </header>
      <ErrorNotice>{error}</ErrorNotice>
      {notice && <p role="status">{notice}</p>}
      <p>{PAYMENT_MESSAGES.negativeBalanceLimitLabel}: {negativeBalanceLimit == null ? '—' : `${fmt(negativeBalanceLimit)} تومان`}</p>
      <form className="card" onSubmit={loadGateway} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: 16 }}>
        <label>{PAYMENT_MESSAGES.vendorIdLabel}
          <input value={vendorId} onChange={(event) => {
            setVendorId(event.target.value);
            setGatewayState(null);
            setConfigIdempotencyKey(newIdempotencyKey());
            setCredentialSetKey(newIdempotencyKey());
            setCredentialRotateKey(newIdempotencyKey());
          }} />
        </label>
        <button className="btn btn-primary" disabled={loading} type="submit">
          {loading ? PAYMENT_MESSAGES.paymentOptionsLoading : PAYMENT_MESSAGES.gatewayLoad}
        </button>
      </form>
      {gatewayState && (
        <section className="card" style={{ display: 'grid', gap: 16, padding: 16, marginTop: 16 }}>
          <dl>
            <div><dt>{PAYMENT_MESSAGES.gatewayEnabledLabel}</dt><dd>{gatewayState.seller_gateway_enabled ? PAYMENT_MESSAGES.gatewayEnabled : PAYMENT_MESSAGES.gatewayDisabled}</dd></div>
            <div><dt>{PAYMENT_MESSAGES.terminalConfiguredLabel}</dt><dd>{gatewayState.terminal_configured ? PAYMENT_MESSAGES.yes : PAYMENT_MESSAGES.no}</dd></div>
            <div><dt>{PAYMENT_MESSAGES.terminalLastDigitsLabel}</dt><dd>{gatewayState.terminal_last_digits || '—'}</dd></div>
          </dl>
          <form onSubmit={saveConfig} style={{ display: 'grid', gap: 12 }}>
            <label>
              <input checked={enabled} onChange={(event) => {
                setEnabled(event.target.checked);
                setConfigIdempotencyKey(newIdempotencyKey());
              }} type="checkbox" />
              {PAYMENT_MESSAGES.gatewayEnabledLabel}
            </label>
            <label>{PAYMENT_MESSAGES.terminalIdLabel}
              <input inputMode="numeric" maxLength={64} pattern="[0-9]*" value={terminalId} onChange={(event) => {
                setTerminalId(event.target.value);
                setConfigIdempotencyKey(newIdempotencyKey());
              }} />
            </label>
            <button className="btn btn-primary" disabled={pending} type="submit">
              {pending ? PAYMENT_MESSAGES.mutationPending : PAYMENT_MESSAGES.gatewaySave}
            </button>
          </form>
          <form onSubmit={(event) => { event.preventDefault(); updateCredential('set'); }} style={{ display: 'grid', gap: 12 }}>
            <label>{PAYMENT_MESSAGES.gatewayCredentialRefLabel}
              <input maxLength={256} value={credentialRef} onChange={(event) => {
                setCredentialRef(event.target.value);
                setCredentialSetKey(newIdempotencyKey());
                setCredentialRotateKey(newIdempotencyKey());
              }} />
            </label>
            <p>{PAYMENT_MESSAGES.gatewayCredentialNeverShown}</p>
            <div className="admin-modal-actions">
              <button className="btn btn-outline" disabled={pending} type="submit">{PAYMENT_MESSAGES.gatewayCredentialSet}</button>
              <button className="btn btn-primary" disabled={pending} onClick={() => updateCredential('rotate')} type="button">
                {PAYMENT_MESSAGES.gatewayCredentialRotate}
              </button>
            </div>
          </form>
        </section>
      )}
    </section>
  );
}

export function AdminAuditLogPage() {
  const [logs, setLogs] = useState([]);
  const offsetRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const loadLogs = useCallback(async (append = false) => {
    const nextOffset = append ? offsetRef.current : 0;
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError('');
    try {
      const { data, error: apiError } = await apiClient.GET(
        '/api/v1/super_admin/settlements/audit-logs',
        { params: { query: { limit: 50, offset: nextOffset } } },
      );
      if (apiError || !Array.isArray(data)) {
        setError(PAYMENT_MESSAGES.auditError);
      } else {
        setLogs((current) => append ? [...current, ...data] : data);
        offsetRef.current = nextOffset + data.length;
        setHasMore(data.length === 50);
      }
    } catch (requestError) {
      console.error('Admin audit log request failed:', requestError);
      setError(PAYMENT_MESSAGES.auditError);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);
  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => { if (active) loadLogs(); }, 0);
    return () => { active = false; window.clearTimeout(timer); };
  }, [loadLogs]);

  return (
    <section>
      <header className="admin-page-header">
        <div><h1>{PAYMENT_MESSAGES.auditTitle}</h1></div>
        <button className="btn btn-outline" disabled={loading} onClick={() => loadLogs()} type="button">
          <RefreshCw size={16} /> {PAYMENT_MESSAGES.auditRefresh}
        </button>
      </header>
      <ErrorNotice>{error}</ErrorNotice>
      {loading ? <p role="status">{PAYMENT_MESSAGES.paymentOptionsLoading}</p> : (
        <>
          {logs.length === 0 && <p>{PAYMENT_MESSAGES.auditEmpty}</p>}
          <Table columns={[
            PAYMENT_MESSAGES.auditDateColumn,
            PAYMENT_MESSAGES.auditActorColumn,
            PAYMENT_MESSAGES.auditActionColumn,
            PAYMENT_MESSAGES.auditTargetColumn,
            PAYMENT_MESSAGES.auditBeforeColumn,
            PAYMENT_MESSAGES.auditAfterColumn,
            PAYMENT_MESSAGES.auditKeyColumn,
          ]}
            rows={logs.map((entry) => (
              <tr key={entry.id}>
                <td>{displayDate(entry.created_at)}</td>
                <td><bdi>{entry.actor_id}</bdi></td>
                <td>{entry.action}</td>
                <td>{entry.target_type} · <bdi>{entry.target_id}</bdi></td>
                <td style={{ maxWidth: 320, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{entry.before_state || '—'}</td>
                <td style={{ maxWidth: 320, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{entry.after_state || '—'}</td>
                <td style={{ maxWidth: 220, overflowWrap: 'anywhere' }}><bdi>{entry.idempotency_key || '—'}</bdi></td>
              </tr>
            ))}
          />
          {hasMore && logs.length > 0 && (
            <button className="btn btn-outline" disabled={loadingMore} onClick={() => loadLogs(true)} type="button">
              {loadingMore ? PAYMENT_MESSAGES.mutationPending : PAYMENT_MESSAGES.auditLoadMore}
            </button>
          )}
        </>
      )}
    </section>
  );
}
